import { useState, useRef } from 'react';
import { fmt$ } from '../lib/samplingEngine';

export default function ScoringPanel({
  project, drawnProjects, scores, onScoreSaved, onComplete, allScored, scoreCount, saving,
}) {
  const [localScores, setLocalScores] = useState(() => {
    const init = {};
    Object.entries(scores).forEach(([k, v]) => { init[k] = v != null ? String(v) : ''; });
    return init;
  });
  const [saving2, setSaving2] = useState({});
  const inFlight = useRef({});

  const handleChange = (id, val) => {
    setLocalScores(prev => ({ ...prev, [String(id)]: val }));
  };

  const handleBlur = async (id, val) => {
    const key = String(id);
    const parsed = parseFloat(val);
    if (val === '' || isNaN(parsed) || parsed < 0 || parsed > 100) return;
    if (inFlight.current[key]) return;
    inFlight.current[key] = true;
    setSaving2(prev => ({ ...prev, [key]: true }));
    try {
      await onScoreSaved(id, parsed);
    } finally {
      inFlight.current[key] = false;
      setSaving2(prev => ({ ...prev, [key]: false }));
    }
  };

  const totalSampleAmt = drawnProjects.reduce((s, p) => s + p.amount, 0);
  const scoredAmt = drawnProjects
    .filter(p => scores[String(p._id)] != null)
    .reduce((s, p) => s + p.amount * (parseFloat(scores[String(p._id)]) / 100), 0);

  const pct = drawnProjects.length ? (scoreCount / drawnProjects.length) * 100 : 0;

  const tagStyle = (isLarge) => ({
    background: isLarge ? '#fff0ee' : '#e8ecf5',
    color: isLarge ? '#c03a1a' : '#0D2461',
    borderRadius: 3, padding: '2px 8px',
    fontFamily: "'Roboto Mono', monospace", fontSize: 10, fontWeight: 600,
  });

  return (
    <div style={card}>
      <div style={eyebrow}>Step 4 of 5</div>
      <h2 style={title}>Project Scoring</h2>
      <p style={sub}>
        Enter the qualification percentage (0–100) for each project. Scores save automatically as you
        move between fields. You can close this window and return at any time — all scores are saved.
      </p>

      {/* Progress */}
      <div style={{ marginBottom: 24 }}>
        <div style={statRow}>
          <div style={stat}><div style={statLabel}>Scored</div><div style={{ ...statVal, color: '#0D2461' }}>{scoreCount} / {drawnProjects.length}</div></div>
          <div style={stat}><div style={statLabel}>Sample Amount</div><div style={{ ...statVal, fontSize: 15 }}>{fmt$(totalSampleAmt)}</div></div>
          <div style={stat}><div style={statLabel}>Qualified So Far</div><div style={{ ...statVal, fontSize: 15, color: '#1a5c3a' }}>{fmt$(scoredAmt)}</div></div>
        </div>
        <div style={{ background: '#e8ecf5', borderRadius: 100, height: 8, overflow: 'hidden', marginBottom: 4 }}>
          <div style={{ height: '100%', background: '#F5A800', borderRadius: 100, width: `${pct}%`, transition: 'width 0.3s' }} />
        </div>
        <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11, color: '#8a96b0', textAlign: 'right' }}>
          {pct.toFixed(0)}% complete
        </div>
      </div>

      {/* Score rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {drawnProjects.map((p, i) => {
          const key = String(p._id);
          const val = localScores[key] ?? '';
          const saved = scores[key] != null;
          const isSaving = saving2[key];
          const qualAmt = saved && val !== '' ? p.amount * (parseFloat(val) / 100) : null;

          return (
            <div key={key} style={{
              ...scoreRow,
              borderColor: saved ? (parseFloat(val) === 0 ? '#c8d0e0' : '#F5A800') : '#c8d0e0',
              background: saved ? (parseFloat(val) === 0 ? '#f4f6fb' : '#fff8e6') : 'white',
              opacity: saved && parseFloat(val) === 0 ? 0.65 : 1,
            }}>
              <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11, color: '#8a96b0', width: 28, flexShrink: 0 }}>
                #{i + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 14, color: '#0D2461', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 3, alignItems: 'center' }}>
                  <span style={tagStyle(p.isLarge)}>{p.isLarge ? '100%' : p.stratum}</span>
                  <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11, color: '#8a96b0' }}>{fmt$(p.amount)}</span>
                  {p.year && <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11, color: '#8a96b0' }}>TY {p.year}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <input
                  style={{
                    width: 72, padding: '7px 8px', border: '1.5px solid',
                    borderColor: saved ? '#F5A800' : '#c8d0e0',
                    borderRadius: '4px', fontFamily: "'Roboto Mono', monospace",
                    fontSize: 14, fontWeight: 600, textAlign: 'right',
                    background: 'white', color: '#0D2461', outline: 'none',
                  }}
                  type="number" min="0" max="100" step="1"
                  placeholder="0–100"
                  value={val}
                  onChange={e => handleChange(p._id, e.target.value)}
                  onBlur={e => handleBlur(p._id, e.target.value)}
                />
                <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 12, color: '#8a96b0' }}>%</span>
                {isSaving && <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 10, color: '#F5A800' }}>saving…</span>}
                {qualAmt !== null && !isSaving && (
                  <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 12, color: '#1a5c3a', width: 90, textAlign: 'right' }}>
                    {fmt$(qualAmt)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={callout}>
        Scores are saved to the database automatically on each entry. Enter a value and press Tab or click
        the next field — no need to click a Save button. Enter <strong>0</strong> for projects that do not qualify.
      </div>

      {allScored && (
        <button style={{ ...primaryBtn, marginTop: 20 }} onClick={onComplete} disabled={saving}>
          {saving ? 'Finalizing…' : 'Complete & Generate Results →'}
        </button>
      )}
      {!allScored && (
        <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 12, color: '#8a96b0', marginTop: 16 }}>
          Score all {drawnProjects.length} projects to proceed to results.
        </div>
      )}
    </div>
  );
}

const card = { background: 'white', border: '1.5px solid #c8d0e0', borderRadius: '8px', padding: '40px' };
const eyebrow = { fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#F5A800', marginBottom: '6px' };
const title = { fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: '24px', color: '#0D2461', margin: '0 0 10px' };
const sub = { fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: '#5a6a8a', lineHeight: 1.7, marginBottom: '24px' };
const statRow = { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 12 };
const stat = { background: '#f4f6fb', border: '1.5px solid #c8d0e0', borderRadius: '6px', padding: '14px 16px' };
const statLabel = { fontFamily: "'Roboto Mono', monospace", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a96b0', marginBottom: 4 };
const statVal = { fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 22, color: '#0D2461' };
const scoreRow = { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: '1.5px solid', borderRadius: '6px', transition: 'all 0.15s' };
const callout = { background: '#e8ecf5', borderLeft: '3px solid #0D2461', borderRadius: '0 6px 6px 0', padding: '14px 18px', fontFamily: "'Roboto Mono', monospace", fontSize: '12px', color: '#0D2461', lineHeight: 1.6 };
const primaryBtn = { padding: '13px 28px', background: '#F5A800', color: '#0D2461', border: 'none', borderRadius: '6px', fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: '14px', cursor: 'pointer' };